import { useEffect, useMemo, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { getOrParseContentBodyAdf } from '@confluence/adf-analysis-core/entry-points/getOrParseContentBodyAdf.ts';
import { useDocumentUpdateStatus } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { setGraphqlErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';

const extractTraceId = (errorHtml: string): string | null => {
	// Extract 32-character hex ID (trace ID format: 946aa64e691d4fab808bb6287f6e1e90)
	// This is localization-independent - works regardless of error message language
	const match = errorHtml.match(/([a-f0-9]{32})/);
	return match ? match[1] : null;
};

export const useADFDocument = (
	adf: string,
	isFabric: boolean,
	adfContentId?: string | null,
): [DocNode, string | null | undefined] => {
	const { documentUpdated } = useDocumentUpdateStatus();
	const docParsed = useMemo(() => {
		let documentParsed: DocNode = {
			version: 1,
			type: 'doc',
			content: [],
		};

		if (adf && isFabric) {
			try {
				if (adfContentId && expValEquals('cc-proj-xl-use-shared-adf-parsing', 'isEnabled', true)) {
					const result = getOrParseContentBodyAdf(adfContentId, adf);
					if (!result.ok) {
						throw result.error;
					}
					documentParsed = result.doc;
				} else {
					documentParsed = JSON.parse(adf);
				}
			} catch {
				let errorString = 'Failed to parse ADF';

				if (
					adf.startsWith('<div class="error fatal-render-error">') &&
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					fg('confluence_include_error_in_adf_parse_failure')
				) {
					errorString += `: ${adf}`;
				}

				const traceIdFromHtml = adf.startsWith('<div class="error fatal-render-error">')
					? extractTraceId(adf)
					: null;

				const error = new Error(errorString);

				if (traceIdFromHtml) {
					setGraphqlErrorAttributes(error, {
						operationName: 'contentBody',
						traceId: traceIdFromHtml,
					});
				}

				throw error;
			}
		}
		return documentParsed;
	}, [adf, adfContentId, isFabric]);

	const [documentState, setDocumentState] = useState(() => ({
		contentId: adfContentId,
		document: docParsed,
	}));

	useEffect(() => {
		setDocumentState((current) =>
			current.document === docParsed && current.contentId === adfContentId
				? current
				: { contentId: adfContentId, document: docParsed },
		);
	}, [adfContentId, docParsed, documentUpdated]);
	return [documentState.document, documentState.contentId];
};
